/**
 * Account Checkout Page
 * Complete subscription checkout with Stripe payment
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, ArrowLeft, Info, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import AccountHeader from '../../components/account/AccountHeader';
import { API_BASE_URL } from '../../config/api';
import { useAccountAuth } from '../../contexts/AccountAuthContext';

// Stripe Payment Form Component
const CheckoutForm = ({ clientSecret, isSetupIntent, isUpgrade, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      let result;
      
      if (isSetupIntent) {
        // For trials - collect card without charging
        result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/app/account/checkout/success`,
          },
        });
      } else {
        // For immediate payment
        result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/app/account/checkout/success`,
          },
        });
      }

      if (result.error) {
        onError(result.error.message);
      } else {
        onSuccess();
      }
    } catch (err) {
      onError(err.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement 
        options={{
          layout: 'tabs',
        }}
      />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full mt-6 py-4 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {processing ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard size={20} />
            {isUpgrade ? 'Complete Upgrade' : (isSetupIntent ? 'Start Free Trial' : 'Subscribe Now')}
          </>
        )}
      </button>
    </form>
  );
};

const AccountCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const plan = location.state?.plan;
  const billingInterval = location.state?.billingInterval || 'monthly';
  const { subscription } = useAccountAuth();
  
  // Check if user has existing subscription (subscription object is non-null when user has active subscription)
  const hasExistingSubscription = !!subscription;
  
  // Get credentials from session storage
  const email = sessionStorage.getItem('account_email') || '';
  const pwd = sessionStorage.getItem('account_pwd') || '';
  
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Stripe states
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isSetupIntent, setIsSetupIntent] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  // Redirect if no plan data
  useEffect(() => {
    if (!plan) {
      navigate('/app/account/plans', { state: { fromPage: 'checkout' } });
      return;
    }
    
    // Initialize Stripe
    fetchStripeConfig();
  }, [plan, navigate]);

  // Calculate price on mount
  useEffect(() => {
    if (plan && email && pwd) {
      calculatePrice();
    }
  }, [plan]);

  const fetchStripeConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/config/stripe`);
      if (response.ok) {
        const data = await response.json();
        if (data.publishable_key) {
          setStripePromise(loadStripe(data.publishable_key));
        }
      }
    } catch (err) {
      console.error('Error fetching Stripe config:', err);
    }
  };

  const calculatePrice = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'calc',
          email: email,
          pwd: pwd,
          plan_id: plan.id,
          inventory_count: 0,
          location_count: 1,
          billing_interval: billingInterval
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setCalculation(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error calculating price:', err);
      setError('Failed to calculate price');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    setError(null);
    
    try {
      // Determine if this is upgrade or new subscription
      const requestType = hasExistingSubscription ? 'upgrade' : 'subscribe';
      
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: requestType,
          email: email,
          pwd: pwd,
          plan_id: plan.id,
          inventory_count: 0,
          location_count: 1,
          billing_interval: billingInterval
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        if (hasExistingSubscription) {
          // Handle upgrade response - using short keys from API
          // rip = requires_immediate_payment
          // rps = requires_payment_setup
          // cs = client_secret (payment_intent or setup_intent)
          // isi = is_setup_intent
          // isc = is_scheduled (deferred downgrade)
          if (result.data.isc) {
            // Scheduled downgrade — no payment needed now
            navigate('/app/account/checkout/success', {
              state: {
                scheduled: true,
                message: result.data.msg,
                scheduledAt: result.data.sat,
                planName: result.data.pn
              }
            });
          } else if ((result.data.rip || result.data.rps) && result.data.cs) {
            setClientSecret(result.data.cs);
            setIsSetupIntent(result.data.isi || result.data.rps);
            setShowPayment(true);
          } else {
            // Upgrade completed without payment needed
            navigate('/app/account/checkout/success');
          }
        } else {
          // Handle new subscription response (existing logic)
          if (result.data.cs) {
            setClientSecret(result.data.cs);
            setIsSetupIntent(result.data.isi);
            setShowPayment(true);
          } else {
            // No payment needed (unlikely but handle it)
            navigate('/app/account/checkout/success');
          }
        }
      } else {
        setError(result.message || 'Failed to process request');
      }
    } catch (err) {
      console.error('Error processing request:', err);
      setError('Failed to process request');
    } finally {
      setSubscribing(false);
    }
  };

  const handlePaymentSuccess = () => {
    // Stripe will redirect to success page
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    setShowPayment(false);
    setClientSecret(null);
  };

  const getButtonText = () => {
    if (hasExistingSubscription) {
      const currentPrice = subscription?.nextPaymentAmount || 0;
      if (plan.price < currentPrice) return 'Downgrade Plan';
      return 'Upgrade Plan';
    }
    if (calculation?.td > 0) {
      return 'Start Free Trial';
    }
    return 'Subscribe Now';
  };

  if (!plan) {
    return null;
  }

  // Stripe appearance options
  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#00E878',
      colorBackground: '#1A1A1A',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'Outfit, system-ui, sans-serif',
      borderRadius: '12px',
    },
  };

  return (
    <div className="min-h-screen bg-dark">
      <AccountHeader title="Checkout" />
      
      <div className="max-w-2xl mx-auto px-4 py-6 pb-32">
        {/* Back Button */}
        <button
          onClick={() => navigate('/app/account/plans', { state: { fromPage: 'checkout' } })}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Plans</span>
        </button>

        {/* Plan Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-3xl font-bold text-white mb-2">{plan.name}</h1>
          <p className="text-gray-400">Complete your subscription</p>
        </motion.div>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
          >
            <p className="text-red-400">{error}</p>
          </motion.div>
        )}

        {/* Stripe Payment Form */}
        {showPayment && clientSecret && stripePromise ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-dark-100 rounded-2xl p-6 border border-white/10 mb-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4">Payment Details</h2>
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance,
              }}
            >
              <CheckoutForm 
                clientSecret={clientSecret}
                isSetupIntent={isSetupIntent}
                isUpgrade={hasExistingSubscription}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          </motion.div>
        ) : (
          <>
            {/* Price Summary Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-dark-100 rounded-2xl p-6 border border-white/10 mb-6"
            >
              <h2 className="text-lg font-semibold text-white mb-4">Price Summary</h2>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-accent animate-spin mr-2" />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : calculation ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Plan</span>
                    <span className="text-white font-medium">{plan.name}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Billing</span>
                    <span className="text-white font-medium capitalize">{billingInterval}</span>
                  </div>
                  
                  <div className="h-px bg-white/10 my-4" />

                  {billingInterval === 'annual' ? (
                    <>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-white font-semibold">You'll be charged</span>
                          <p className="text-xs text-gray-500 mt-0.5">Renews annually</p>
                        </div>
                        <span className="text-2xl font-bold text-accent">
                          ${(calculation.cmp * 12).toFixed(2)}/yr
                        </span>
                      </div>

                      {calculation.bf > calculation.cmp && (
                        <div className="mt-3 p-3 bg-dark-200 rounded-xl">
                          <p className="text-sm text-gray-400">
                            💰 That's just <span className="text-white font-medium">${calculation.cmp?.toFixed(2)}/mo</span> — you're saving{' '}
                            <span className="text-accent font-semibold">
                              ${((calculation.bf - calculation.cmp) * 12).toFixed(0)}/year
                            </span>
                            {' '}compared to monthly billing
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-white font-semibold">You'll be charged</span>
                        <p className="text-xs text-gray-500 mt-0.5">Renews monthly</p>
                      </div>
                      <span className="text-2xl font-bold text-accent">
                        ${calculation.cmp?.toFixed(2)}/mo
                      </span>
                    </div>
                  )}
                  
                  {calculation.td > 0 && (
                    <div className="mt-4 p-4 bg-accent/10 rounded-xl border border-accent/30">
                      <p className="text-accent font-semibold">🎉 {calculation.td} days free trial</p>
                      <p className="text-gray-400 text-sm mt-1">
                        You won't be charged {billingInterval === 'annual' ? `$${(calculation.cmp * 12).toFixed(2)}` : `$${calculation.cmp?.toFixed(2)}`} until the trial ends. Cancel anytime.
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </motion.div>

            {/* What's Included Section */}
            {plan.displayFeatures?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-dark-100 rounded-2xl p-6 border border-white/10 mb-6"
              >
                <h2 className="text-lg font-semibold text-white mb-4">What's Included</h2>
                
                <ul className="space-y-3">
                  {plan.displayFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Terms Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl mb-6"
            >
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-400/80">
                  By subscribing, you agree to our Terms of Service and Privacy Policy. 
                  Your subscription will automatically renew each month.
                </p>
              </div>
            </motion.div>
          </>
        )}
      </div>

      {/* Fixed Footer - Only show when not in payment mode */}
      {!showPayment && (
        <div className="fixed bottom-0 left-0 right-0 bg-dark-100 border-t border-white/10 p-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={handleSubscribe}
              disabled={subscribing || loading || !calculation}
              className="w-full py-4 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {subscribing ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Creating Subscription...
                </>
              ) : (
                getButtonText()
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountCheckout;
